import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Users, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamInvitationBanner = ({ token, onInvitationHandled }) => {
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState({});

    useEffect(() => {
        fetchPendingInvitations();
    }, [token]);

    const fetchPendingInvitations = async () => {
        try {
            const res = await axios.get(`${API}/teams/invitations/pending`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInvitations(res.data.invitations || []);
        } catch (err) {
            console.error('Failed to fetch invitations', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async (invitation) => {
        setProcessing(prev => ({ ...prev, [invitation.id]: 'accepting' }));
        try {
            await axios.post(`${API}/teams/invitations/${invitation.id}/accept`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Successfully joined ${invitation.team_name}!`);
            setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
            if (onInvitationHandled) onInvitationHandled();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to accept invitation');
        } finally {
            setProcessing(prev => ({ ...prev, [invitation.id]: null }));
        }
    };

    const handleReject = async (invitation) => {
        setProcessing(prev => ({ ...prev, [invitation.id]: 'rejecting' }));
        try {
            await axios.post(`${API}/teams/invitations/${invitation.id}/reject`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Invitation declined');
            setInvitations(prev => prev.filter(inv => inv.id !== invitation.id));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to decline invitation');
        } finally {
            setProcessing(prev => ({ ...prev, [invitation.id]: null }));
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-purple-100 text-purple-700';
            case 'editor': return 'bg-blue-100 text-blue-700';
            case 'billing': return 'bg-green-100 text-green-700';
            case 'viewer': return 'bg-gray-100 text-gray-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    if (loading || invitations.length === 0) {
        return null;
    }

    return (
        <div className="space-y-3 mb-6">
            {invitations.map((invitation) => (
                <div 
                    key={invitation.id}
                    className="bg-amber-50 border border-amber-200 rounded-lg p-4"
                    data-testid="team-invitation-banner"
                >
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <Users className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-900">
                                Team Invitation
                            </h3>
                            <p className="mt-1 text-sm text-gray-700">
                                <strong>{invitation.inviter?.name || invitation.inviter?.email || 'A team owner'}</strong> has invited you to join{' '}
                                <strong>{invitation.team_name}</strong>
                                {invitation.website_domain && (
                                    <span className="text-gray-500"> ({invitation.website_domain})</span>
                                )}
                                {' '}as a{' '}
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${getRoleBadgeColor(invitation.role)}`}>
                                    {invitation.role}
                                </span>
                            </p>
                        </div>
                        <div className="flex-shrink-0 flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReject(invitation)}
                                disabled={processing[invitation.id]}
                                className="border-gray-300 text-gray-700 hover:bg-gray-100"
                                data-testid="reject-invitation-btn"
                            >
                                {processing[invitation.id] === 'rejecting' ? (
                                    <span className="animate-spin">...</span>
                                ) : (
                                    <>
                                        <X className="h-4 w-4 mr-1" />
                                        Decline
                                    </>
                                )}
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => handleAccept(invitation)}
                                disabled={processing[invitation.id]}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                data-testid="accept-invitation-btn"
                            >
                                {processing[invitation.id] === 'accepting' ? (
                                    <span className="animate-spin">...</span>
                                ) : (
                                    <>
                                        <Check className="h-4 w-4 mr-1" />
                                        Accept
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default TeamInvitationBanner;
